import React from "react";
import styled, { css } from "styled-components";

import { IViewProps, View } from "core/decorators/View";
import { iDef } from "core/util/TypeUtils";
import Icon from "../icon/Icon";
import PagingViewConfig from "./PagingViewConfig";

/**
 * generic controlled paging input component
 *
 * @author chills
 */

export enum PagingViewVariant {
	DEFAULT = "default"
}

interface IPagingViewProps extends IViewProps<PagingViewConfig> {
	pageNumber: number;
	pageSize: number;
	setPageNumber: (pageNumber: number) => void;
	total: number;
}

interface IPagingWrapperProps {
	config: PagingViewConfig;
}

const PagingWrapper = styled.div<IPagingWrapperProps>(({config}) => css`
	display: flex;
	justify-content: flex-start;

	& > a {
		display: block;
		border: ${config.border};
		background-color: ${config.backgroundColor};
		color: ${config.color || 'inherit'};
		padding: ${config.buttonPadding};
		border-radius: 0;
	}

	& > a + a {
		border-left-width: 0;
	}

	& > :first-child {
		border-top-left-radius: ${config.borderRadius};
		border-bottom-left-radius: ${config.borderRadius};
	}

	& > :last-child {
		border-top-right-radius: ${config.borderRadius};
		border-bottom-right-radius: ${config.borderRadius};
	}

	& > a:not(.disabled):hover {
		background-color: ${config.hoverBackgroundColor};
		color: ${config.color};
	}

	& > a:active,
	& > a.active {
		background-color: ${config.activeBackgroundColor};
		color: ${config.activeColor};
	}

	& > a.disabled {
		cursor: default;
		background-color: ${config.disabledBackgroundColor};
		color: ${config.disabledTextColor};
	}
`);

@View({
	id: "paging",
	configClass: PagingViewConfig,
	variants: PagingViewVariant
})
export default class PagingView extends React.PureComponent<IPagingViewProps> {

	public render() {
		const { config, pageNumber = 0, pageSize, total = 0 } = this.props;

		if (pageSize < 1 || total < 1) {
			return null;
		}

		const pages: number[] = [];

		for (let i = 0, len = Math.ceil(total / pageSize); i < len; i++) {
			pages.push(i);
		}

		// also don't show if only one page?
		if (pages.length < 2) {
			return null;
		}

		const prevDisabled = pageNumber < 1;
		const nextDisabled = pageNumber >= pages.length - 1;

		return (
			<PagingWrapper config={config}>
				{
					config.showFirstLast && (
						<a key="first" className={prevDisabled ? 'disabled' : undefined} onClick={!prevDisabled ? this.setPageNumber(0) : undefined}>
							<Icon icon="double-chevron-left" />
						</a>
					)
				}
				{
					config.showNextPrev && (
						<a key="prev" className={prevDisabled ? 'disabled' : undefined} onClick={!prevDisabled ? this.setPageNumber(pageNumber - 1) : undefined}>
							<Icon icon="chevron-left" />
						</a>
					)
				}
				{
					config.showNumbers && this.getVisiblePageList(pages).map((page) => (
						<a key={page} className={page === pageNumber ? 'active' : undefined} onClick={this.setPageNumber(page)}>
							{(page + 1).toString()}
						</a>
					))
				}
				{
					config.showNextPrev && (
						<a key="next" className={nextDisabled ? 'disabled' : undefined} onClick={!nextDisabled ? this.setPageNumber(pageNumber + 1) : undefined}>
							<Icon icon="chevron-right" />
						</a>
					)
				}
				{
					config.showFirstLast && (
						<a key="last" className={nextDisabled ? 'disabled' : undefined} onClick={!nextDisabled ? this.setPageNumber(pages.length - 1) : undefined}>
							<Icon icon="double-chevron-right" />
						</a>
					)
				}
			</PagingWrapper>
		);
	}

	private getVisiblePageList = (pages: number[]) => {
		const { config, pageNumber = 0 } = this.props;

		// maximum visible pages
		const maxPages: number = (
			iDef(config.visiblePageNumbers) && config.visiblePageNumbers > 0 ? config.visiblePageNumbers : 5
		);

		// if less pages than maximum, no overflow
		if (maxPages > pages.length) {
			return pages;
		}

		const padding: number = Math.floor(maxPages / 2);

		let start = pageNumber > padding ? pageNumber - padding : 0;
		let end = start + maxPages;

		if (end > (pages.length - padding + 1)) {
			end = pages.length;
			start = end - maxPages;
		}

		return pages.slice(start, end);
	}

	private setPageNumber = (n: number) => () => {
		const { setPageNumber } = this.props;
		setPageNumber(n);
	}
}



// WEBPACK FOOTER //
// ./src/common/views/paging/Paging.view.tsx